<script lang="ts" generics="ROW">
    import type {Cell} from '@tanstack/svelte-table';
    import {getGridContext} from '../grid.context';
    import {isLastColumnInMainColumnGroup} from '../utils';
    import {setGridCellContext} from './grid-cell.context';
    import {exists} from 'shared/utils/custom-utils';
    import {distinctStore} from 'shared/utils/store-utils';
    import {CellCoordinates} from '../types';
    import {
        isBottomBorder,
        isCellInEditMode,
        isCursor,
        isHighlightedDependency,
        isHoverOrCursorRow,
        isLeftBorder,
        isRightBorder,
        isSelected,
        isTopBorder,
        isVirtualCursor
    } from '../state/state-query';

    export let cell: Cell<ROW, unknown>;
    export let rowIndex: number;
    export let columnIndex: number;
    export let baseBackgroundColor: 'light' | 'dark';

    let gridCellRef: HTMLElement;
    let thisCellCoordinates = new CellCoordinates({rowIndex, columnIndex});

    const {
        settings: {columnPinningEnabled, mainGroupsDivided, columnResizingEnabled, condensed},
        stateManager: {state$}
    } = getGridContext();

    const coordinates$ = distinctStore(thisCellCoordinates);
    const {contentDependentState$} = setGridCellContext(coordinates$);

    $: isColumnGroupBorder = mainGroupsDivided && isLastColumnInMainColumnGroup(cell.column);
    $: pinnedLeft = columnPinningEnabled && cell.column.getIsPinned() === 'left';
    $: leftOffset = (columnPinningEnabled || columnResizingEnabled) ? `${cell.column.getStart('left')}px` : '0px';
    $: thisCellCoordinates = new CellCoordinates({rowIndex, columnIndex});
    $: coordinates$.set(thisCellCoordinates);
    $: hoverOrCursorRow = isHoverOrCursorRow($state$, thisCellCoordinates);
    $: cursor = isCursor($state$, thisCellCoordinates);
    $: selected = isSelected($state$, thisCellCoordinates);
    $: virtualCursor = isVirtualCursor($state$, thisCellCoordinates);
    $: editModeActive = isCellInEditMode($state$, thisCellCoordinates);
    $: topBorder = isTopBorder($state$, thisCellCoordinates);
    $: rightBorder = isRightBorder($state$, thisCellCoordinates);
    $: bottomBorder = isBottomBorder($state$, thisCellCoordinates);
    $: leftBorder = isLeftBorder($state$, thisCellCoordinates);
    $: border = topBorder || rightBorder || bottomBorder || leftBorder;
    $: invalid = $contentDependentState$.invalid;
    $: disabled = $contentDependentState$.disabled;
    $: readOnly = $contentDependentState$.readOnly;
    $: highlightedDependency = isHighlightedDependency($state$, thisCellCoordinates) && !cursor;
    $: if (cursor && !editModeActive && exists(gridCellRef)) {
        gridCellRef.focus();
    }
</script>

<div role="gridcell"
     tabindex="-1"
     bind:this={gridCellRef}
     class="kp-generic-grid-cell"
     data-row-index={rowIndex}
     data-column-index={columnIndex}
     title="[{rowIndex},{columnIndex}]"
     class:light={baseBackgroundColor === 'light'}
     class:dark={baseBackgroundColor === 'dark'}
     class:column-group-border={isColumnGroupBorder}
     class:pinned-left={pinnedLeft}
     class:active={cursor || virtualCursor}
     class:hover={hoverOrCursorRow}
     class:selected
     class:invalid
     class:disabled
     class:condensed
     class:read-only={readOnly}
     class:highlighted-dependency={highlightedDependency}
     style:width="{cell.column.getSize()}px"
     style:--cell-width="{cell.column.getSize()}px"
     style:--left-offset={leftOffset}>

    <div class="cell-content" class:condensed>
        <slot/>
    </div>

    {#if !editModeActive}
        {#if highlightedDependency}
            <div class="cell-inner-outline highlighted-dependency top"></div>
            <div class="cell-inner-outline highlighted-dependency right"></div>
            <div class="cell-inner-outline highlighted-dependency bottom"></div>
            <div class="cell-inner-outline highlighted-dependency left"></div>
        {/if}

        {#if cursor || border}
            {#if cursor || topBorder}
                <div class="cell-outer-outline top" class:wide={cursor}></div>
            {/if}

            {#if cursor || rightBorder}
                <div class="cell-outer-outline right" class:wide={cursor}></div>
            {/if}

            {#if cursor || bottomBorder}
                <div class="cell-outer-outline bottom" class:wide={cursor}></div>
            {/if}

            {#if cursor || leftBorder}
                <div class="cell-outer-outline left" class:wide={cursor}></div>
            {/if}
        {/if}
    {/if}

    {#if !disabled && readOnly}
        <div class="read-only-mark" title="Pouze pro čtení" aria-label="Pouze pro čtení"></div>
    {/if}

    {#if !disabled && invalid}
        <span class="required-asterisk" title="Povinný údaj" aria-label="Povinný údaj">*</span>
    {/if}

    {#if virtualCursor}
        <div class="virtual-cursor"></div>
    {/if}
</div>

<style lang="less">
    @import (reference) "styles/portaro.themes.less";

    @default-border-width: 1px;
    @default-row-height: 36px; // including border
    @condensed-row-height: 32px; // including border

    .kp-generic-grid-cell {
        position: relative;
        flex: none; // row is flexbox, cell is fixed size

        --cell-heigth: @default-row-height;
        contain: style size;
        contain-intrinsic-size: var(--cell-width, 150px) var(--cell-heigth);
        height: var(--cell-heigth);

        z-index: calc(var(--default-cell-z-index) * var(--z-index-active-multiplier, 1) * var(--z-index-pinned-multiplier, 1));
        border-bottom: @default-border-width solid @themed-border-muted;
        border-right: var(--col-border-width, @default-border-width) solid @themed-border-muted;

        &.condensed {
            --cell-heigth: @condensed-row-height;
        }

        &.light {
            --base-light: #fff;
            background: var(--base-light);

            &.hover {
                --hover-color: #f0f3ff;
                background: var(--hover-color);
            }

            &.selected {
                --selected-color: #f8ece9;
                background: var(--selected-color);
            }

            &.disabled {
                --hatch-color: #ccc;
                --bg-color: var(--base-light);
                .hatch-effect();
                cursor: not-allowed;
            }

            &.hover.selected {
                --hover-selected-color: #e9e0e8;
                background: var(--hover-selected-color);
            }

            &.hover.disabled {
                --bg-color: var(--hover-color);
                .hatch-effect();
            }

            &.selected.disabled {
                --bg-color: var(--selected-color);
                .hatch-effect();
            }

            &.hover.selected.disabled {
                --bg-color: var(--hover-selected-color);
                .hatch-effect();
            }
        }

        &.dark {
            --base-light: #f6f6f6;
            background: var(--base-light);

            &.hover {
                --hover-color: #e8eaf5;
                background: var(--hover-color);
            }

            &.selected {
                --selected-color: #efe3e0;
                background: var(--selected-color);
            }

            &.disabled {
                --hatch-color: #bbb;
                --bg-color: var(--base-light);
                .hatch-effect();
                cursor: not-allowed;
            }

            &.hover.selected {
                --hover-selected-color: #e3d8e0;
                background: var(--hover-selected-color);
            }

            &.hover.disabled {
                --bg-color: var(--hover-color);
                .hatch-effect();
            }

            &.selected.disabled {
                --bg-color: var(--selected-color);
                .hatch-effect();
            }

            &.hover.selected.disabled {
                --bg-color: var(--hover-selected-color);
                .hatch-effect();
            }
        }

        &.pinned-left {
            --z-index-pinned-multiplier: var(--pinned-columns-z-index-coefficient);
            position: sticky;
            left: var(--left-offset, 0px);
        }

        &.active {
            --z-index-active-multiplier: var(--active-cell-z-index-coefficient);
        }

        &.column-group-border {
            --col-border-width: 2px;
        }

        &.read-only {
            color: var(--text-muted);;

        }

        .read-only-mark {
            position: absolute;
            top: 0;
            right: 0;
            width: 10px;
            height: 10px;
            background: #bbb; /* grey color */
            clip-path: polygon(0% 0%, 100% 0%, 100% 100%);
            cursor: help;
        }

        .required-asterisk {
            position: absolute;
            top: 5px;
            right: 5px;
            color: red;
            cursor: help;
        }

        .cell-outer-outline {
            position: absolute;
            z-index: 1;
            background-color: var(--brand-orange);

            &.wide {
                --outline-width: 3px;
            }

            &.top {
                top: -1px;
                left: -1px;
                height: var(--outline-width, 2px);
                width: calc(100% + 2px);
            }

            &.right {
                top: -1px;
                right: -1px;
                height: calc(100% + 2px);
                width: var(--outline-width, 2px);
            }

            &.bottom {
                bottom: -1px;
                left: -1px;
                height: var(--outline-width, 2px);
                width: calc(100% + 2px);
            }

            &.left {
                top: -1px;
                left: -1px;
                height: calc(100% + 2px);
                width: var(--outline-width, 2px);
            }
        }

        .cell-inner-outline {
            position: absolute;
            @offset: 2px;

            &.highlighted-dependency {
                --outline-color: #9747FF;
            }

            &.top {
                top: @offset;
                left: @offset;
                height: var(--outline-width, 2px);
                width: calc(100% - 2 * @offset);
                .dash-pattern-horizontal();
            }

            &.right {
                top: @offset;
                right: @offset;
                height: calc(100% - 2 * @offset);
                width: var(--outline-width, 2px);
                .dash-pattern-vertical();
            }

            &.bottom {
                bottom: @offset;
                left: @offset;
                height: var(--outline-width, 2px);
                width: calc(100% - 2 * @offset);
                .dash-pattern-horizontal();
            }

            &.left {
                top: @offset;
                left: @offset;
                height: calc(100% - 2 * @offset);
                width: var(--outline-width, 2px);
                .dash-pattern-vertical();
            }
        }

        .virtual-cursor {
            @cursor-size: 8px;

            position: absolute;
            z-index: 10;
            height: @cursor-size;
            width: @cursor-size;
            bottom: 0;
            right: 0;
            background-color: var(--brand-orange);

            opacity: 0.5;
        }

        .cell-content {
            height: 100%;
            width: 100%;
            padding: var(--default-grid-cell-padding);
            line-height: var(--default-line-height);
            white-space: nowrap;
            align-content: center;
            overflow: hidden;
            background: transparent;

            &.condensed {
                padding: var(--condensed-grid-cell-padding);
                font-size: 12px;
            }
        }
    }

    .hatch-effect() {
        background: linear-gradient(-45deg,
        var(--hatch-color) 5%,
        var(--bg-color) 5%,
        var(--bg-color) 50%,
        var(--hatch-color,) 50%,
        var(--hatch-color) 55%,
        var(--bg-color) 55%,
        var(--bg-color) 100%) 0 0 / 10px 10px repeat;
    }

    .dash-pattern-horizontal() {
        background-image: linear-gradient(90deg,
        var(--outline-color, transparent) 25%,
        transparent 25%,
        transparent 50%,
        var(--outline-color, transparent) 50%,
        var(--outline-color, transparent) 75%,
        transparent 75%,
        transparent 100%);
        background-size: 15px 15px;
    }

    .dash-pattern-vertical() {
        background-image: linear-gradient(180deg,
        var(--outline-color, transparent) 25%,
        transparent 25%,
        transparent 50%,
        var(--outline-color, transparent) 50%,
        var(--outline-color, transparent) 75%,
        transparent 75%,
        transparent 100%);
        background-size: 15px 15px;
    }
</style>
